import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  LocalShipping as ContainerIcon,
  EventNote as PlanningIcon,
  CalendarViewMonth as InspectionsIcon,
  Assessment as ReportsIcon,
  Receipt as POReportsIcon,
  Upload as UploadIcon,
  NotificationsActive as AlertIcon,
  DateRange as DateRangeIcon,
  Warehouse as WarehouseIcon,
  PrecisionManufacturing as ProductionIcon,
  QrCodeScanner as BatchTraceIcon
} from '@mui/icons-material';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

interface ContainerNavProps {
  title?: string;
  onImportClick?: () => void;
  onAlertsClick?: () => void;
  showImportButton?: boolean;
  showAlertsButton?: boolean;
  rightContent?: React.ReactNode;
}

const navItems: NavItem[] = [
  { label: 'Containers', path: '/containers', icon: <ContainerIcon /> },
  { label: 'Container Planning', path: '/containers/planning', icon: <PlanningIcon /> },
  { label: 'Container Inspections', path: '/containers/forklift', icon: <InspectionsIcon /> },
  { label: 'Container Reports', path: '/containers/reports', icon: <ReportsIcon /> },
  { label: 'PO Reports', path: '/containers/po-reports', icon: <POReportsIcon /> },
  { label: 'Exposure', path: '/containers/exposure', icon: <DateRangeIcon /> },
  { label: 'Batch Trace', path: '/containers/batch-trace', icon: <BatchTraceIcon /> }
];

const ContainerNav: React.FC<ContainerNavProps> = ({
  title,
  onImportClick,
  onAlertsClick,
  showImportButton = false,
  showAlertsButton = false,
  rightContent
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
      pb={2}
      borderBottom="1px solid"
      borderColor="divider"
    >
      <Box display="flex" alignItems="center" gap={2}>
        {title && (
          <Typography variant="h5" component="h1" sx={{ mr: 2 }}>
            {title}
          </Typography>
        )}

        {/* Navigation Buttons */}
        <Box display="flex" alignItems="center" gap={1}>
          {navItems.map((item) => (
            <Button
              key={item.path}
              onClick={() => navigate(item.path)}
              variant={isActive(item.path) ? 'contained' : 'outlined'}
              color="primary"
              size="small"
              startIcon={item.icon}
              sx={{
                textTransform: 'none',
                fontWeight: isActive(item.path) ? 'bold' : 'normal',
                minWidth: 'auto',
                px: 2
              }}
            >
              {item.label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Right side actions */}
      <Box display="flex" alignItems="center" gap={1}>
        {showImportButton && onImportClick && (
          <Tooltip title="Import Open Orders from Prism ERP" arrow>
            <IconButton
              onClick={onImportClick}
              sx={{
                border: '2px solid',
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'white'
                }
              }}
              size="medium"
            >
              <UploadIcon />
            </IconButton>
          </Tooltip>
        )}

        {showAlertsButton && onAlertsClick && (
          <Tooltip title="Scan for Alerts" arrow>
            <IconButton
              onClick={onAlertsClick}
              sx={{
                border: '2px solid',
                borderColor: 'warning.main',
                color: 'warning.main',
                '&:hover': {
                  bgcolor: 'warning.main',
                  color: 'white'
                }
              }}
              size="medium"
            >
              <AlertIcon />
            </IconButton>
          </Tooltip>
        )}

        {rightContent}

        {/* Cross-link to WMS */}
        <Button
          onClick={() => navigate('/wms/intake')}
          variant="outlined"
          size="small"
          startIcon={<WarehouseIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            ml: 1,
          }}
        >
          WMS
        </Button>

        {/* Cross-link to Production Scheduling */}
        <Button
          onClick={() => navigate('/production/board')}
          variant="outlined"
          size="small"
          startIcon={<ProductionIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            ml: 1,
          }}
        >
          Production
        </Button>
      </Box>
    </Box>
  );
};

export default ContainerNav;
